import { useEffect, useRef } from "react";

/**
 * Tracks dwell time on the current page for visitors arriving with UTM tags.
 * Sends a beacon to /api/public/track/social-click on unload (or after 60s
 * heartbeat) so the social analytics dashboard can show time-on-page per item.
 */
export function useSocialDwellTracker(opts: {
  kind: "news" | "story";
  itemId?: string | null;
  itemSlug?: string | null;
}) {
  const sentRef = useRef(false);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get("utm_source");
    const utm_medium = params.get("utm_medium");
    const utm_campaign = params.get("utm_campaign");
    // Only track when arriving from an attributed share.
    if (!utm_source && !utm_campaign) return;

    startRef.current = Date.now();

    const send = (extra?: { dwell_ms?: number }) => {
      if (sentRef.current) return;
      sentRef.current = true;
      try {
        const payload = JSON.stringify({
          kind: opts.kind,
          item_id: opts.itemId ?? null,
          item_slug: opts.itemSlug ?? null,
          utm_source,
          utm_medium,
          utm_campaign,
          referrer: document.referrer || null,
          dwell_ms: extra?.dwell_ms ?? Date.now() - startRef.current,
        });
        const url = "/api/public/track/social-click";
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            url,
            new Blob([payload], { type: "application/json" }),
          );
        } else {
          fetch(url, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
        /* ignore */
      }
    };

    const onHide = () => {
      if (document.visibilityState === "hidden") send();
    };
    const onPageHide = () => send();

    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      send();
    };
  }, [opts.kind, opts.itemId, opts.itemSlug]);
}
